import { useState, useEffect } from "react";

const C = {
  gold:"#b48c3c", goldD:"#8a6f2e", goldBg:"rgba(180,140,60,0.15)",
  text:"#1a1a2e", muted:"#6b7280", border:"#e8e4df", light:"#f3f4f6",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

export function MiniCalendar({ currentDayStr, setDayOffset, appointments, TODAY }) {
  const [viewMonth, setViewMonth] = useState(() => {
    const d = new Date(currentDayStr || TODAY);
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  // Owner wants the square calendar on every device — phones included.
  // Stack it above the schedule on narrow screens instead of side-by-side.
  // Treat iPad portrait (≤1024px) the same as phone: stack the calendar
  // on TOP of the schedule using full width, instead of pinning it to a
  // 220px left strip that leaves big white space on the right.
  const [isPhone, setIsPhone] = useState(() => typeof window !== "undefined" && window.innerWidth <= 1024);
  useEffect(() => {
    const onResize = () => setIsPhone(window.innerWidth <= 1024);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthName = viewMonth.toLocaleDateString("en-US", {month:"long", year:"numeric"});
  
  // Sunday-first week (US convention) — firstDay is 0..6 where 0 = Sunday,
  // so we just use it directly to know how many leading empty cells to pad.
  const cells = [];
  const firstAdjusted = firstDay;
  for (let i = 0; i < firstAdjusted; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  
  const goPrev = () => setViewMonth(new Date(year, month - 1, 1));
  const goNext = () => setViewMonth(new Date(year, month + 1, 1));
  
  // Format a Date as LOCAL "YYYY-MM-DD" (not UTC). Without this, tapping a
  // day after ~8 PM EDT would jump to the next day because .toISOString() is
  // UTC-based and EDT is UTC-4.
  const toLocalStr = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const clickDay = (day) => {
    const clickedDate = new Date(year, month, day);
    const today = new Date();
    today.setHours(0,0,0,0);
    const diff = Math.round((clickedDate - today) / (1000*60*60*24));
    setDayOffset(diff);
  };
  
  const apptsToday = appointments.filter(a => a.date === currentDayStr && a.status !== "cancelled").length;
  
  return (
    <div style={{
      padding: isPhone ? "10px 12px 8px" : "14px",
      borderRight: isPhone ? "none" : "1px solid "+C.border,
      borderBottom: isPhone ? "1px solid "+C.border : "none",
      background:"#fafaf8",
      minWidth: isPhone ? "auto" : 220,
      width: isPhone ? "100%" : 220,
      boxSizing:"border-box",
    }}>
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10}}>
        <button onClick={goPrev} style={{padding:"4px 10px", background:C.gold, color:"#fff", border:"none", borderRadius:6, cursor:"pointer", fontSize:14, fontWeight:700}}>‹</button>
        <div style={{fontSize:13, fontWeight:700, color:C.gold, fontFamily:S}}>{monthName}</div>
        <button onClick={goNext} style={{padding:"4px 10px", background:C.gold, color:"#fff", border:"none", borderRadius:6, cursor:"pointer", fontSize:14, fontWeight:700}}>›</button>
      </div>
      
      <div style={{display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:2, marginBottom:4}}>
        {["S","M","T","W","T","F","S"].map((d, i) => (
          <div key={i} style={{textAlign:"center", fontSize:9, fontWeight:700, color:C.muted, padding:"4px 0"}}>{d}</div>
        ))}
      </div>
      
      <div style={{display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:2}}>
        {cells.map((day, idx) => {
          if (!day) return <div key={"e"+idx} />;
          const cellDate = new Date(year, month, day);
          const cellStr = toLocalStr(cellDate);
          const isSelected = cellStr === currentDayStr;
          const isToday = cellStr === TODAY;
          const hasAppts = appointments.some(a => a.date === cellStr && a.status !== "cancelled");
          
          return (
            <button
              key={day}
              onClick={() => clickDay(day)}
              style={{
                // On wide screens keep the square look; on tablet/phone use a
                // shorter rectangle (4:3) so the whole calendar takes ~30%
                // less vertical space and the schedule below stays visible.
                aspectRatio: isPhone ? "1.4" : "1",
                border:"none",
                borderRadius:6,
                background: isSelected ? C.gold : isToday ? C.goldBg : "transparent",
                color: isSelected ? "#fff" : isToday ? C.gold : C.text,
                fontSize: isPhone ? 14 : 12,
                fontWeight: isSelected || isToday ? 800 : 500,
                cursor:"pointer",
                fontFamily: F,
                position:"relative"
              }}
              onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = C.light; }}
              onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = isToday ? C.goldBg : "transparent"; }}
            >
              {day}
              {hasAppts && !isSelected && (
                <div style={{position:"absolute", bottom:2, left:"50%", transform:"translateX(-50%)", width:4, height:4, borderRadius:"50%", background:C.gold}} />
              )}
            </button>
          );
        })}
      </div>
      
      {/* On phone/tablet show a single compact row with both Today + count;
          on desktop keep the original stacked layout. */}
      {isPhone ? (
        <div style={{display:"flex",gap:8,marginTop:8,alignItems:"center"}}>
          <button onClick={() => setDayOffset(0)} style={{flex:1,padding:"7px",background:"#fff",color:C.gold,border:"1px solid "+C.border,borderRadius:6,cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:F}}>Today</button>
          <div style={{padding:"7px 12px",background:"#fff",borderRadius:7,border:"1px solid "+C.border,fontSize:11,color:C.muted,whiteSpace:"nowrap"}}>
            <strong style={{color:C.gold}}>{apptsToday}</strong> appt{apptsToday!==1?"s":""}
          </div>
        </div>
      ) : (
        <>
          <button onClick={() => setDayOffset(0)} style={{width:"100%", marginTop:10, padding:"7px", background:"#fff", color:C.gold, border:"1px solid "+C.border, borderRadius:6, cursor:"pointer", fontSize:11, fontWeight:700, fontFamily:F}}>Jump to Today</button>
          <div style={{marginTop:12, padding:"10px", background:"#fff", borderRadius:7, border:"1px solid "+C.border, fontSize:11, color:C.muted, textAlign:"center"}}>
            <strong style={{color:C.gold}}>{apptsToday}</strong> appointment{apptsToday!==1?"s":""} on selected day
          </div>
        </>
      )}
    </div>
  );
}
